import { useQuery } from "@tanstack/react-query";
import { useTacticHooks } from "../../providers/TacticHooksProvider";
import {
  type CollectedSchedules,
  collectSchedulePages,
  SCHEDULE_PAGE_SIZE
} from "@gettactic/helpers/src/schedules/collectSchedulePages";

export interface UseResourceReservationsParams {
  resourceId: string | undefined;
  /** Inclusive range start, `yyyy-MM-dd`. */
  start: string;
  /** Inclusive range end, `yyyy-MM-dd`. */
  end: string;
  /** Timezone the range is anchored in. */
  tz: string;
  enabled?: boolean;
  /**
   * `admin` (default) is the resource-update listing. `dedicated` is the same
   * listing for a dedicated desk, which the desk's own dedicated user may read
   * too, so they can see what releasing the desk would cancel.
   */
  scope?: "admin" | "dedicated";
}

/**
 * React Query key for {@link useResourceReservations}.
 *
 * `tz` is part of the key because it is part of the request: the same resource
 * and date strings resolve to a different window in a different timezone. The
 * timezone can change without the dates changing — the caller derives it from
 * the resource's office and falls back to the device zone until that resolves —
 * and omitting it would serve the first window's cached bookings for the
 * second, with no refetch to signal it.
 */
export const resourceReservationsQueryKey = (
  resourceId: string | undefined,
  start: string,
  end: string,
  tz: string,
  scope: "admin" | "dedicated" = "admin"
) => ["schedules", "byResource", resourceId, start, end, tz, scope];

/**
 * Every reservation on one resource in a date range, whoever made it.
 *
 * Requires the resource-update permission — this is for the admin prompts that
 * warn what a change to a resource would disrupt. Pages through the results so
 * a long recurring series cannot silently cut the list short.
 */
export const useResourceReservations = ({
  resourceId,
  start,
  end,
  tz,
  enabled = true,
  scope = "admin"
}: UseResourceReservationsParams) => {
  const { apiClient } = useTacticHooks();
  return useQuery({
    queryKey: resourceReservationsQueryKey(resourceId, start, end, tz, scope),
    queryFn: async (): Promise<CollectedSchedules> => {
      if (!resourceId) return { schedules: [], isTruncated: false };
      const list =
        scope === "dedicated"
          ? apiClient.schedules.forDedicatedResource.bind(apiClient.schedules)
          : apiClient.schedules.forResource.bind(apiClient.schedules);
      return collectSchedulePages((cursor) =>
        list(resourceId, {
          start,
          end,
          tz,
          limit: SCHEDULE_PAGE_SIZE,
          cursor
        })
      );
    },
    enabled: enabled && !!resourceId && !!start && !!end && !!tz
  });
};
